import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { format, addDays } from 'date-fns';
import { tr, de, enUS } from 'date-fns/locale';
import { toast } from 'sonner';
import { PageSeo } from '@/components/seo/PageSeo';
import { 
  Building2, Search, Calendar, Users, MapPin, Star, 
  Wifi, Car, Coffee, Utensils, Dumbbell, Waves,
  ChevronDown, Filter, Loader2, Heart, Info, X,
  Check, RefreshCw, BedDouble
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
  DrawerClose,
} from '../components/ui/drawer';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { tursysHotels } from '../lib/api';

// Hook to detect mobile
function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < breakpoint : false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

const HotelSearchPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  // Search form state
  const [destination, setDestination] = useState('');
  const [destinationId, setDestinationId] = useState(null);
  const [destinationType, setDestinationType] = useState(20);
  const [checkIn, setCheckIn] = useState(addDays(new Date(), 7));
  const [checkOut, setCheckOut] = useState(addDays(new Date(), 14));
  const [guests, setGuests] = useState({ adults: 2, children: 0, childAges: [], rooms: 1 });
  const [dateDrawerOpen, setDateDrawerOpen] = useState(false);
  
  // Autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const autocompleteTimeout = useRef(null);
  
  // Search results
  const [hotels, setHotels] = useState([]);
  const [poolSearchId, setPoolSearchId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchInfo, setSearchInfo] = useState(null);
  
  // Filters
  const [sortBy, setSortBy] = useState('price');
  const [minStars, setMinStars] = useState(0);
  const [onlyRefundable, setOnlyRefundable] = useState(false);
  
  // Popular destinations with Tursys IDs
  const popularDestinations = [
    { name: 'Antalya', country: 'Türkiye', id: '65d81595140d7c0fef897185', type: 20, image: 'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=300&h=200&fit=crop' },
    { name: 'Istanbul', country: 'Türkiye', id: '65d81595140d7c0fef896f53', type: 20, image: 'https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?w=300&h=200&fit=crop' },
    { name: 'Bodrum', country: 'Türkiye', id: '65d81595140d7c0fef896ee7', type: 20, image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=300&h=200&fit=crop' },
    { name: 'Fethiye', country: 'Türkiye', id: '65d81595140d7c0fef896f01', type: 30, image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=300&h=200&fit=crop' },
    { name: 'Marmaris', country: 'Türkiye', id: '65d81595140d7c0fef8970d5', type: 30, image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=300&h=200&fit=crop' },
    { name: 'Alanya', country: 'Türkiye', id: '65d81595140d7c0fef896e7d', type: 30, image: 'https://images.unsplash.com/photo-1584132967334-10e028bd69f7?w=300&h=200&fit=crop' },
    { name: 'Cesme', country: 'Türkiye', id: '65d81595140d7c0fef896eba', type: 30, image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=300&h=200&fit=crop' },
    { name: 'Kemer', country: 'Türkiye', id: '65d81595140d7c0fef896fa7', type: 30, image: 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=300&h=200&fit=crop' },
    { name: 'Berlin', country: 'Almanya', id: '65d814f4140d7c0fef875d15', type: 20, image: 'https://images.unsplash.com/photo-1560969184-10fe8719e047?w=300&h=200&fit=crop' },
    { name: 'München', country: 'Almanya', id: '65d814f2140d7c0fef875791', type: 30, image: 'https://images.unsplash.com/photo-1595867818082-083862f3d630?w=300&h=200&fit=crop' },
    { name: 'Amsterdam', country: 'Hollanda', id: '65d814f4140d7c0fef875ca5', type: 30, image: 'https://images.unsplash.com/photo-1534351590666-13e3e96b5017?w=300&h=200&fit=crop' },
    { name: 'Paris', country: 'Fransa', id: '65d814f4140d7c0fef875f9d', type: 30, image: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=300&h=200&fit=crop' },
  ];
  
  // Date locale
  const getDateLocale = () => {
    switch(i18n.language) {
      case 'tr': return tr;
      case 'de': return de;
      default: return enUS;
    }
  };
  
  // Autocomplete search
  const handleAutocomplete = useCallback(async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    
    setLoadingSuggestions(true);
    try {
      const lang = i18n.language === 'tr' ? 'TR' : (i18n.language === 'de' ? 'DE' : 'EN');
      const response = await tursysHotels.autocomplete(query, lang);
      if (response.data?.success) {
        setSuggestions(response.data.results || []);
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error('Autocomplete error:', error);
    } finally {
      setLoadingSuggestions(false);
    }
  }, [i18n.language]);
  
  // Debounced autocomplete
  const onDestinationChange = (e) => {
    const value = e.target.value;
    setDestination(value);
    setDestinationId(null);
    
    if (autocompleteTimeout.current) {
      clearTimeout(autocompleteTimeout.current);
    }
    
    autocompleteTimeout.current = setTimeout(() => {
      handleAutocomplete(value);
    }, 300);
  };
  
  // Select suggestion
  const selectSuggestion = (suggestion) => {
    setDestination(suggestion.name);
    setDestinationId(suggestion.id);
    setDestinationType(suggestion.type);
    setShowSuggestions(false);
    setSuggestions([]);
  };
  
  // Update guest count
  const updateGuests = (type, delta) => {
    setGuests(prev => {
      const newCount = Math.max(type === 'rooms' ? 1 : 0, prev[type] + delta);
      if (type === 'adults' && newCount < 1) return prev;
      if (newCount > 10) return prev;
      return { ...prev, [type]: newCount };
    });
  };
  
  // Search hotels
  const handleSearch = async (destId = null, destType = null) => {
    const searchDestId = destId || destinationId;
    const searchDestType = destType || destinationType;
    
    if (!searchDestId) {
      toast.error(t('hotelSearch.selectDestination') || 'Lütfen listeden bir destinasyon seçin');
      return;
    }
    
    setLoading(true);
    setSearched(true);
    
    try {
      // Build room configuration
      const rooms = [];
      for (let i = 0; i < guests.rooms; i++) {
        rooms.push({
          adult: Math.ceil(guests.adults / guests.rooms),
          childAges: guests.childAges
        });
      }
      
      const response = await tursysHotels.search({
        destination_id: searchDestId,
        destination_type: searchDestType,
        check_in: format(checkIn, 'yyyy-MM-dd'),
        check_out: format(checkOut, 'yyyy-MM-dd'),
        rooms: rooms,
        nationality: 'DE',
        currency: 'EUR',
        language: i18n.language === 'tr' ? 'TR' : (i18n.language === 'de' ? 'DE' : 'EN'),
        hotel_count: 200,  // Increased to include 5-star hotels
        only_refundable: onlyRefundable,
        min_stars: minStars  // Apply star filter
      });
      
      if (response.data?.success) {
        setHotels(response.data.hotels || []);
        setPoolSearchId(response.data.poolSearchId);
        setSearchInfo(response.data.searchInfo);
        
        if ((response.data.hotels || []).length === 0) {
          toast.info(t('hotelSearch.noResults') || 'Bu tarihler için otel bulunamadı');
        }
      } else {
        toast.error(response.data?.error?.[0] || 'Arama hatası');
        setHotels([]);
      }
      
    } catch (error) {
      console.error('Hotel search error:', error);
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.error?.[0] ||
        error?.message;
      toast.error(
        typeof detail === 'string'
          ? detail
          : (t('hotelSearch.searchFailed') || 'Otel araması başarısız oldu')
      );
      setHotels([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle popular destination click
  const handlePopularDestination = (dest) => {
    setDestination(dest.name);
    setDestinationId(dest.id);
    setDestinationType(dest.type);
    handleSearch(dest.id, dest.type);
  };
  
  // Sort hotels
  const getSortedHotels = () => {
    let filtered = [...hotels];
    
    // Filter by stars
    if (minStars > 0) {
      filtered = filtered.filter(h => h.stars >= minStars);
    }
    
    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'price') return (a.price?.total || 0) - (b.price?.total || 0);
      if (sortBy === 'stars') return (b.stars || 0) - (a.stars || 0);
      if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
      return 0;
    });
    
    return filtered;
  };
  
  // Calculate nights
  const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
  
  // Handle hotel selection - navigate to detail page
  const handleSelectHotel = (hotel) => {
    // Store search context in sessionStorage
    sessionStorage.setItem('hotelSearchContext', JSON.stringify({
      poolSearchId,
      searchRequest: {
        destination_id: destinationId,
        destination_type: destinationType,
        check_in: format(checkIn, 'yyyy-MM-dd'),
        check_out: format(checkOut, 'yyyy-MM-dd'),
        rooms: [{ adult: guests.adults, childAges: guests.childAges }],
        nationality: 'DE',
        currency: 'EUR',
        language: i18n.language === 'tr' ? 'TR' : 'DE'
      },
      hotel: hotel,
      guests,
      nights
    }));
    
    navigate(`/hotels/${hotel.id}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white">
      <PageSeo title={t('hotelSearch.title')} description={t('hotelSearch.subtitle')} segments={['hotels']} />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <Building2 className="w-10 h-10" />
            <h1 className="text-3xl font-bold">{t('hotelSearch.title') || 'Hotel Reservation'}</h1>
          </div>
          <p className="text-indigo-100 text-lg">{t('hotelSearch.subtitle') || 'Book hotels at the best prices'}</p>
        </div>
      </div>
      
      {/* Search Form */}
      <div className="container mx-auto px-4 -mt-6">
        <Card className="shadow-xl border-0">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Destination with Autocomplete */}
              <div className="lg:col-span-1 relative">
                <Label className="text-sm text-gray-600 mb-1 block">{t('hotelSearch.destination') || 'Where are you going?'}</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={destination}
                    onChange={onDestinationChange}
                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                    placeholder={t('hotelSearch.destinationPlaceholder') || 'City or hotel name'}
                    className="pl-10"
                    data-testid="hotel-destination-input"
                  />
                  {loadingSuggestions && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
                  )}
                  {destinationId && (
                    <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                  )}
                </div>
                
                {/* Autocomplete Suggestions */}
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    {suggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        className="w-full px-4 py-3 text-left hover:bg-indigo-50 flex items-center gap-3 border-b last:border-b-0"
                        onClick={() => selectSuggestion(suggestion)}
                      >
                        <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div>
                          <div className="font-medium">{suggestion.name}</div>
                          <div className="text-xs text-gray-500">{suggestion.typeName}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Dates */}
              <div className="lg:col-span-1">
                <Label className="text-sm text-gray-600 mb-1 block">{t('hotelSearch.checkInOut') || 'Check-in - Check-out'}</Label>
                
                {/* Mobile: Drawer */}
                {isMobile ? (
                  <>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => setDateDrawerOpen(true)}
                      data-testid="date-picker-trigger"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      {format(checkIn, 'd MMM', { locale: getDateLocale() })} - {format(checkOut, 'd MMM', { locale: getDateLocale() })}
                      <span className="ml-auto text-xs text-muted-foreground">{nights} {t('hotelSearch.nights') || 'nights'}</span>
                    </Button>
                    <Drawer open={dateDrawerOpen} onOpenChange={setDateDrawerOpen}>
                      <DrawerContent className="max-h-[85vh]">
                        <DrawerHeader className="pb-2">
                          <DrawerTitle className="flex items-center gap-2">
                            <Calendar className="w-5 h-5" />
                            {t('hotelSearch.checkInOut') || 'Check-in - Check-out'}
                          </DrawerTitle>
                        </DrawerHeader>
                        <div className="overflow-y-auto px-4 pb-4 space-y-4">
                          <div>
                            <Label className="text-sm font-medium text-gray-700 mb-2 block">{t('hotelSearch.checkIn') || 'Check-in'}</Label>
                            <div className="flex justify-center">
                              <CalendarComponent
                                mode="single"
                                selected={checkIn}
                                onSelect={(date) => {
                                  if (date) {
                                    setCheckIn(date);
                                    if (date >= checkOut) {
                                      setCheckOut(addDays(date, 1));
                                    }
                                  }
                                }}
                                disabled={(date) => date < new Date()}
                                locale={getDateLocale()}
                                className="rounded-lg border"
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-sm font-medium text-gray-700 mb-2 block">{t('hotelSearch.checkOut') || 'Check-out'}</Label>
                            <div className="flex justify-center">
                              <CalendarComponent
                                mode="single"
                                selected={checkOut}
                                onSelect={(date) => date && setCheckOut(date)}
                                disabled={(date) => date <= checkIn}
                                locale={getDateLocale()}
                                className="rounded-lg border"
                              />
                            </div>
                          </div>
                        </div>
                        <DrawerFooter className="pt-2">
                          <div className="flex items-center justify-between w-full px-2 py-2 bg-indigo-50 rounded-lg mb-2">
                            <span className="text-sm text-gray-600">{format(checkIn, 'd MMM', { locale: getDateLocale() })} - {format(checkOut, 'd MMM', { locale: getDateLocale() })}</span>
                            <Badge variant="secondary">{nights} {t('hotelSearch.nights') || 'nights'}</Badge>
                          </div>
                          <DrawerClose asChild>
                            <Button className="w-full bg-indigo-600 hover:bg-indigo-700">
                              {t('hotelSearch.confirmDates', 'Tarihleri Onayla')}
                            </Button>
                          </DrawerClose>
                        </DrawerFooter>
                      </DrawerContent>
                    </Drawer>
                  </>
                ) : (
                  /* Desktop: Popover */
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start" data-testid="date-picker-trigger">
                        <Calendar className="w-4 h-4 mr-2" />
                        {format(checkIn, 'd MMM', { locale: getDateLocale() })} - {format(checkOut, 'd MMM', { locale: getDateLocale() })}
                        <span className="ml-auto text-xs text-muted-foreground">{nights} {t('hotelSearch.nights') || 'nights'}</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <div className="flex">
                        <div className="p-3">
                          <Label className="text-xs text-gray-500 mb-1 block">{t('hotelSearch.checkIn') || 'Check-in'}</Label>
                          <CalendarComponent
                            mode="single"
                            selected={checkIn}
                            onSelect={(date) => {
                              if (date) {
                                setCheckIn(date);
                                if (date >= checkOut) {
                                  setCheckOut(addDays(date, 1));
                                }
                              }
                            }}
                            disabled={(date) => date < new Date()}
                            locale={getDateLocale()}
                          />
                        </div>
                        <div className="p-3 border-l">
                          <Label className="text-xs text-gray-500 mb-1 block">{t('hotelSearch.checkOut') || 'Check-out'}</Label>
                          <CalendarComponent
                            mode="single"
                            selected={checkOut}
                            onSelect={(date) => date && setCheckOut(date)}
                            disabled={(date) => date <= checkIn}
                            locale={getDateLocale()}
                          />
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
              
              {/* Guests */}
              <div className="lg:col-span-1">
                <Label className="text-sm text-gray-600 mb-1 block">{t('hotelSearch.guestsAndRooms') || 'Guests & Rooms'}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <Users className="w-4 h-4 mr-2" />
                      {guests.adults + guests.children} {t('hotelSearch.guests') || 'Guests'}, {guests.rooms} {t('hotelSearch.room') || 'Room'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72">
                    <div className="space-y-4">
                      {/* Adults */}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{t('hotelSearch.adults') || 'Adults'}</div>
                          <div className="text-xs text-gray-500">18+</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => updateGuests('adults', -1)}
                          >-</Button>
                          <span className="w-8 text-center">{guests.adults}</span>
                          <Button 
                            variant="outline" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => updateGuests('adults', 1)}
                          >+</Button>
                        </div>
                      </div>
                      
                      {/* Children */}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{t('hotelSearch.children') || 'Children'}</div>
                          <div className="text-xs text-gray-500">0-17</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => updateGuests('children', -1)}
                          >-</Button>
                          <span className="w-8 text-center">{guests.children}</span>
                          <Button 
                            variant="outline" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => updateGuests('children', 1)}
                          >+</Button>
                        </div>
                      </div>
                      
                      {/* Rooms */}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{t('hotelSearch.room') || 'Room'}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => updateGuests('rooms', -1)}
                          >-</Button>
                          <span className="w-8 text-center">{guests.rooms}</span>
                          <Button 
                            variant="outline" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => updateGuests('rooms', 1)}
                          >+</Button>
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              
              {/* Search Button */}
              <div className="lg:col-span-1 flex items-end">
                <Button 
                  onClick={() => handleSearch()}
                  disabled={loading || !destinationId}
                  className="w-full py-6 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                  data-testid="search-hotels-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {t('hotelSearch.searching') || 'Searching...'}
                    </>
                  ) : (
                    <>
                      <Search className="w-5 h-5 mr-2" />
                      {t('hotelSearch.searchHotels') || 'Search Hotels'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Popular Destinations (show when no search) */}
      {!searched && (
        <div className="container mx-auto px-4 py-12">
          <h2 className="text-2xl font-bold mb-6">{t('hotelSearch.popularDestinations') || 'Popular Destinations'}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {popularDestinations.map((dest, idx) => (
              <Card 
                key={idx} 
                className="cursor-pointer hover:shadow-lg transition-shadow overflow-hidden"
                onClick={() => handlePopularDestination(dest)}
                data-testid={`destination-${dest.name.toLowerCase()}`}
              >
                <div className="relative h-32">
                  <img 
                    src={dest.image} 
                    alt={dest.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-2 left-2 text-white">
                    <div className="font-semibold">{dest.name}</div>
                    <div className="text-xs opacity-80">{dest.country}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
      
      {/* Search Results */}
      {searched && !loading && (
        <div className="container mx-auto px-4 py-8">
          {/* Results Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold">{destination} {t('hotelSearch.hotels') || 'Hotels'}</h2>
              <p className="text-gray-600">
                {format(checkIn, 'd MMMM', { locale: getDateLocale() })} - {format(checkOut, 'd MMMM', { locale: getDateLocale() })} • 
                {nights} {t('hotelSearch.nights') || 'nights'} • {getSortedHotels().length} {t('hotelSearch.hotelsFound') || 'hotels found'}
              </p>
            </div>
            
            <div className="flex gap-2">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder={t('hotelSearch.sort') || 'Sort'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="price">{t('hotelSearch.sortByPrice') || 'Price'}</SelectItem>
                  <SelectItem value="stars">{t('hotelSearch.sortByStars') || 'Stars'}</SelectItem>
                  <SelectItem value="name">{t('hotelSearch.sortByName') || 'Name'}</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={minStars.toString()} onValueChange={(v) => setMinStars(parseInt(v))}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder={t('hotelSearch.stars') || 'Stars'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">{t('hotelSearch.allStars') || 'All'}</SelectItem>
                  <SelectItem value="3">3+ {t('hotelSearch.stars') || 'Stars'}</SelectItem>
                  <SelectItem value="4">4+ {t('hotelSearch.stars') || 'Stars'}</SelectItem>
                  <SelectItem value="5">5 {t('hotelSearch.stars') || 'Stars'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Info Notice */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <Info className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-indigo-800 font-medium">{t('hotelSearch.priceInfo') || 'Price Information'}</p>
              <p className="text-indigo-600 text-sm">
                {t('hotelSearch.priceDescription') || `Prices shown are total for ${nights} nights including taxes.`}
              </p>
            </div>
          </div>
          
          {/* Hotel Cards - Grid Layout: 8 columns */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
            {getSortedHotels().map((hotel) => (
              <Card 
                key={hotel.id} 
                className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1 overflow-hidden cursor-pointer group"
                data-testid={`hotel-card-${hotel.id}`}
                onClick={() => handleSelectHotel(hotel)}
              >
                {/* Image */}
                <div className="relative h-32 bg-gray-100">
                  {hotel.mainImage ? (
                    <img 
                      src={hotel.mainImage} 
                      alt={hotel.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&h=300&fit=crop';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                      <Building2 className="w-10 h-10 text-gray-300" />
                    </div>
                  )}
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  
                  {/* Favorite button */}
                  <button 
                    className="absolute top-1.5 right-1.5 p-1 bg-white/80 rounded-full hover:bg-white transition-colors"
                    onClick={(e) => { e.stopPropagation(); }}
                  >
                    <Heart className="w-3.5 h-3.5 text-gray-600" />
                  </button>
                  
                  {/* Stars badge */}
                  {hotel.stars > 0 && (
                    <div className="absolute top-1.5 left-1.5 flex items-center gap-0.5 bg-white/90 rounded-full px-1.5 py-0.5">
                      {[...Array(hotel.stars)].map((_, i) => (
                        <Star key={i} className="w-2.5 h-2.5 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                  )}
                  
                  {/* Price overlay */}
                  <div className="absolute bottom-1.5 right-1.5 bg-indigo-600 text-white px-1.5 py-0.5 rounded text-sm font-bold">
                    €{(hotel.price?.total || 0).toFixed(0)}
                  </div>
                </div>
                
                {/* Content */}
                <CardContent className="p-2">
                  {/* Hotel name */}
                  <h3 className="font-semibold text-xs line-clamp-2 mb-1 group-hover:text-indigo-600 transition-colors leading-tight">
                    {hotel.name}
                  </h3>
                  
                  {/* Location */}
                  <p className="text-gray-500 text-xs flex items-center gap-0.5 mb-1">
                    <MapPin className="w-2.5 h-2.5 flex-shrink-0" />
                    <span className="truncate">{hotel.address?.city || destination}</span>
                  </p>
                  
                  {/* Room Info - Compact */}
                  {hotel.rooms && hotel.rooms.length > 0 && (
                    <div className="flex items-center gap-1 mb-1">
                      <Badge variant="secondary" className="text-xs px-1 py-0 h-4">
                        <BedDouble className="w-2.5 h-2.5 mr-0.5" />
                        {(hotel.rooms[0]?.name || 'Room').substring(0, 10)}...
                      </Badge>
                      {hotel.rooms[0]?.isRefundable && (
                        <RefreshCw className="w-3 h-3 text-green-600" />
                      )}
                    </div>
                  )}
                  
                  {/* Price per night */}
                  <div className="flex items-center justify-between pt-1 border-t text-xs">
                    <span className="text-gray-400">{nights}g</span>
                    {hotel.price?.perNight && (
                      <span className="text-indigo-600 font-medium">
                        €{hotel.price.perNight.toFixed(0)}/g
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {getSortedHotels().length === 0 && (
              <div className="col-span-full text-center py-12 text-gray-500">
                <Building2 className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg">{t('hotelSearch.noResults') || 'No hotels found for these criteria'}</p>
                <p className="text-sm">{t('hotelSearch.tryDifferent') || 'Please try different dates or destination'}</p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Loading State */}
      {loading && (
        <div className="container mx-auto px-4 py-12 text-center">
          <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-indigo-600" />
          <p className="text-lg text-gray-600">{t('hotelSearch.searchingBest') || 'Searching for the best hotels...'}</p>
        </div>
      )}
    </div>
  );
};

export default HotelSearchPage;
